import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { nextTick } from "vue";

import getProfile, { ProfileResponse } from "@/modules/authorisation/api/getProfile";
import { getAuthClient } from "@/modules/authorisation/authClient";
import { useFunderStore } from "@/modules/funder/store";
import dayjs from "@/shared/plugins/dayjs";

import { Permission, Role, Token } from "./types";

export const useAuthorisationStore = defineStore("authorisation", {
  persist: true,
  state: (): {
    user?: {
      email?: string;
      name?: string;
      permissions?: Permission[];
      roles?: Role[];
      featureFlags?: { [key: string]: boolean };
    };
    userPreferences?: Record<string, any>;
    redirectToAfterLoginUrl?: string;
    profile?: ProfileResponse;
    profileLastFetchedAt?: string;
    currentFunderId?: string;
  } => ({
    user: undefined,
    userPreferences: { isDarkMode: null },
    redirectToAfterLoginUrl: undefined,
    profileLastFetchedAt: undefined,
    profile: undefined,
    currentFunderId: undefined
  }),
  actions: {
    setUserPreference(key: string, value: any) {
      if (!this.userPreferences) {
        this.userPreferences = {};
      }
      this.userPreferences[key] = value;
    },
    async afterTokenReceived() {
      const authClient = await getAuthClient();
      const isAuthenticated = await authClient.isAuthenticated();
      if (!isAuthenticated) {
        await authClient.login();
        throw new Error("User is not authenticated, while trying to handle afterTokenReceived");
      }

      const accessToken = await authClient.getToken();

      if (!accessToken) {
        await authClient.login();
        throw new Error("Access token is missing after authentication");
      }
      const payload = jwtDecode<Token>(accessToken);
      const kindeUser = authClient.getUser();

      this.user = {
        email: kindeUser.email || undefined,
        name: kindeUser.given_name || undefined,
        permissions: payload.permissions,
        roles: payload.roles,
        featureFlags: Object.entries(payload?.feature_flags || {})
          .map(([key, value]) => ({ [key]: value.v }))
          .reduce(
            (acc, cur) => ({
              ...acc,
              ...cur
            }),
            {}
          )
      };
      this.currentFunderId = undefined;
    },
    async fetchProfileInformation(force: boolean = false) {
      if (this.profileLastFetchedAt && dayjs().diff(this.profileLastFetchedAt, "minutes") < 5 && !force) {
        return;
      }
      try {
        this.profileLastFetchedAt = dayjs().toISOString();
        const response = await getProfile();
        this.profile = response.data;
      } catch {
        this.profileLastFetchedAt = undefined;
      }
    },
    async selectFunder(funderId: string) {
      const funderStore = useFunderStore();
      this.currentFunderId = funderId;
      await nextTick();
      funderStore.data = undefined;
      await funderStore.forceFetchFunder();
    },
    setCurrentFunderProfile(data: Partial<ProfileResponse["funders"][0]["profile"]>) {
      const index = this.currentFunderId
        ? this.profile?.funders?.findIndex(({ funderId }) => funderId === this.currentFunderId)
        : 0;

      if (index === undefined || index === -1 || !this.profile) {
        return;
      }

      this.profile.funders[index].profile = {
        ...this.profile.funders[index].profile,
        ...data
      };
    }
  },
  getters: {
    hasFeature(state) {
      return (flag: string) => state.user?.featureFlags?.[flag] ?? false;
    },
    hasRole(state) {
      return (role: string) => state.user?.roles?.some(({ key }) => key === role) ?? false;
    },
    currentFunderProfile(state) {
      if (!state.currentFunderId) {
        return state.profile?.funders?.[0]?.profile;
      }
      return state.profile?.funders?.find(({ funderId }) => funderId === state.currentFunderId)?.profile;
    },
    hasMultipleFunders(state) {
      return (state.profile?.funders?.length || 0) > 1;
    },
    getRedirectToAfterLoginUrl(state) {
      if (
        state.redirectToAfterLoginUrl &&
        !["/auth/login", "/404", "/403"].some((path) => state.redirectToAfterLoginUrl!.includes(path))
      ) {
        return state.redirectToAfterLoginUrl;
      }
    }
  }
});
